"use client"

import { useNavigate } from "react-router-dom"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import GenericFeedback from "@fuse/sharedComponents/GenericFeedback"
import { useImageModal } from "@fuse/sharedComponents/globalImageModal"
import Bottom from "@fuse/sharedComponents/legacyUI/Bottom"
import Navbar from "@fuse/sharedComponents/navbar/Navbar"
import { ROUTES } from "src/App"

import {
  DownloadLinkMap,
  onOpenDownloadLink,
  useDeviceDetection,
} from "@fuse/utils/DeviceDection"

interface ErrorStateProps {
  contentType: keyof typeof EventModalContentMap
  userId: string
}
type TButton = { buttonText: string; onPress: any }
type TContent = {
  title: string
  description: string | React.ReactNode
  buttons: EEventModalButtons[]
  emoji: string | JSX.Element
}

export default function EventNotFound({ userId }: { userId: string }) {
  return (
    <div
      style={{
        background: `url(/assets/alpha-event-not-found.svg)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      className="flex flex-col min-h-screen  "
    >
      <BlurWrapper
        className="flex flex-col flex-1"
        overlayOpacityDefault="10"
        overlayColorDefault="dark"
      >
        <Navbar />
        <ErrorState
          contentType={EModalShowContentMap.NOT_FOUND}
          userId={userId}
        />
        <div className="mt-auto">
          <Bottom />
        </div>
      </BlurWrapper>
    </div>
  )
}

const getTheContent = (type: keyof typeof EventModalContentMap): TContent => {
  return EventModalContentMap[type]
}

export function ErrorState({ contentType, userId }: ErrorStateProps) {
  const { hideImageAlert } = useImageModal()

  const navigate = useNavigate()
  const buttonContent = {
    goToHomepage: {
      buttonText: "Go to homepage",
      onPress: () => {
        navigate(ROUTES.LANDING)
        hideImageAlert()
      },
    },
    gotIt: {
      buttonText: "Got it",
      onPress: () => {
        hideImageAlert()
      },
    },
  }
  const content: TContent = getTheContent(contentType)

  return (
    <div className="h-full flex-1 my-auto w-full flex bg-transparent items-center justify-center p-4">
      <BlurWrapper
        overlayOpacityDefault="15"
        overlayColorDefault="light"
        className="min-w-[700px] rounded-[32px] flex items-center justify-center min-h-[500px] max-w-[700px]"
      >
        <div className=" w-full p-[56px]">
          <div className="flex flex-col items-center text-center">
            <div className="text-6xl">{content.emoji}</div>
            <h1 className="text-[32px] font-semibold mt-[32px] text-white">
              {content.title}
            </h1>

            <p className="text-gray-300 font-[400] text-[24px] px-[30px] mt-[24px]">
              {content?.description}
            </p>

            {content?.buttons.map((item, _) => (
              <button
                key={_}
                onClick={buttonContent[item].onPress}
                className="px-[66px] mt-[56px] py-[18px] bg-gray-600 rounded-full hover:bg-gray-60 blurTypeLight border border-white/20 ring-1 ring-gray-900/5 text-white"
              >
                {buttonContent[item].buttonText}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <GenericFeedback fromScreen="Choose Ticket" userId={userId} />
          </div>
        </div>
      </BlurWrapper>
    </div>
  )
}

export function useShowAlertWithContentType() {
  const { showImageAlert, hideImageAlert } = useImageModal()
  const navigate = useNavigate()

  const buttonContent = {
    goToHomepage: {
      buttonText: "Go to homepage",
      onPress: () => {
        navigate(ROUTES.LANDING)
        hideImageAlert()
      },
    },
    gotIt: {
      buttonText: "Got it",
      onPress: () => {
        hideImageAlert()
      },
    },
  }

  const showContentTypeAlert = (content: TContent) => {
    return showImageAlert({
      image: content?.emoji as JSX.Element,
      title: content?.title,
      description: content?.description,
      okButtonTitle: buttonContent[content?.buttons[0]]?.buttonText,
      okButtonPress: buttonContent[content?.buttons[0]]?.onPress,
      cancelButtonTitle: buttonContent[content?.buttons[1]]?.buttonText,
      cancelButtonPress: buttonContent[content?.buttons[1]]?.onPress,
    })
  }

  return { showContentTypeAlert }
}

export enum EModalShowContentMap {
  "NOT_FOUND" = "notFound",
  "RESTRICTED" = "restricted",
  "CANCELLED" = "cancelled",
  "DECLINED" = "declined",
  "CLOSED" = "closed",
  "REQ_DECLINED" = "declined_user_request",
}

export const enum EEventModalButtons {
  // DOWNLOAD_APP = "downloadApp",
  GO_TO_HOMEPAGE = "goToHomepage",
  GOT_IT = "gotIt",
}

export const EventModalContentMap = {
  declined_user_request: {
    title: "We’re really sorry",
    description:
      "Your request to join {{eventName}} was declined.\n\n We’ve issued your ticket refund. It usually processes in around 5-10 business days. \n\nThanks for your understanding!",
    emoji: (
      <img
        src="/assets/alpha-sad.gif"
        alt="emoji"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [EEventModalButtons.GO_TO_HOMEPAGE],
  },
  notFound: {
    title: "Event not found",
    description:
      "Oops! We couldn't find the event you're looking for. Please try again later.",
    emoji: (
      <img
        src="/assets/event-not-found-icon.gif"
        alt="emoji"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [EEventModalButtons.GO_TO_HOMEPAGE],
  },
  restricted: {
    title: "We're really sorry",
    description:
      "It seems you can't join this event right now.\n\nIt might be full, past the sign-up time, or there could be some specific requirements and restrictions.\n\nThanks for your understanding!",
    emoji: (
      <img
        src="/assets/alpha-event-cancelled.gif"
        alt="restricted"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [EEventModalButtons.GO_TO_HOMEPAGE],
  },

  cancelled: {
    title: "",
    description:
      "We're sorry, the host of {{eventName}} has cancelled the event.\n\nThanks for your understanding!",
    emoji: (
      <img
        src="/assets/alpha-event-cancelled.gif"
        alt="cancelled"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [EEventModalButtons.GO_TO_HOMEPAGE],
  },
  closed: {
    title: "",
    description:
      "We're sorry, but this event is no longer open for participation. \n\n It looks like the sign-up has ended or the event is fully booked.\n\nThanks for your understanding!",
    emoji: (
      <img
        src="/assets/alpha-event-cancelled.gif"
        alt="closed"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [
      // EEventModalButtons.DOWNLOAD_APP,
      EEventModalButtons.GOT_IT,
    ],
  },
  refundInitiated: {
    title: "",
    description:
      "We're sorry, the host of Tech Talk: Innovations & Insights has cancelled the event.\n\nNo worries, we've already processed your ticket refund! You should see it in your account within 5-10 business days.\n\nThanks for your understanding!",
    emoji: (
      <img
        src="/assets/alpha-event-cancelled.gif"
        alt="refund-initiated"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [EEventModalButtons.GOT_IT],
  },
  refundProcessed: {
    title: "",
    description: (
      <>
        <p className="mb-4 text-white/50 font-thin">
          Your refund is on its way and should arrive in 5-10 business days!
        </p>
        <p className="mb-4 text-white/50 font-thin">
          If you have any questions or issues, here's your reference ID:
        </p>
        <div className="flex mb-4 w-[100%] justify-center items-center">
          <p className=" text-white font-thin">{`{{refId}}`}</p>
          <div
            onClick={() => navigator.clipboard.writeText("{{refId}}")}
            className="ml-2 text-white/50 hover:text-white"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
              />
            </svg>
          </div>
        </div>
        <p className="mb-4 text-white/50 font-thin">
          We hope to see you at another event soon! Download the app to explore
          more events or create your own!
        </p>
      </>
    ),
    emoji: (
      <img
        src="/assets/alpha-event-refund.gif"
        alt="emoji"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [EEventModalButtons.GOT_IT],
  },
  refunded: {
    title: "Ticket refunded",
    description: (
      <>
        <p className="mb-4 text-white/50 font-thin">
          If you have any questions or issues, here’s your reference ID:
        </p>
        <div className="flex mb-4 w-[100%] justify-center items-center">
          <p className=" text-white font-thin">{`{{refId}}`}</p>
          <div
            onClick={() => navigator.clipboard.writeText("{{refId}}")}
            className="ml-2 text-white/50 hover:text-white"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
              />
            </svg>
          </div>
        </div>
        <p className="mb-4 text-white/50 font-thin">
          We hope to see you at another event soon! Download the app to explore
          more events or create your own!
        </p>
      </>
    ),
    emoji: (
      <img
        src="/assets/alpha-event-refund.gif"
        alt="emoji"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [EEventModalButtons.GO_TO_HOMEPAGE],
  },
  declined: {
    title: "",
    description:
      "We're sorry, the host of Tech Talk: Innovations & Insights has cancelled the event.\n\nDownload the app to explore more events or create your own!",
    emoji: (
      <img
        src="/assets/alpha-sad.gif"
        alt="emoji"
        className="w-[128px] mx-auto"
      />
    ),
    buttons: [],
  },
}
